import React, { useState, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';

export default function SearchableDropdown({ options, value, onChange, placeholder, label }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation(); // Prevent dropdown from toggling when clicking clear
    onChange(null);
    setSearchTerm('');
    if (isOpen) {
        inputRef.current?.focus();
    }
  };

  return (
    <div className="sd-dropdown-container" ref={dropdownRef}>
      {/* Dropdown Toggle Button */}
      <button
        type="button"
        className={`sd-toggle-btn ${isOpen ? 'is-open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="sd-toggle-text">
          {value ? value : placeholder}
        </span>
        
        <div className="sd-icon-container">
            {value && (
                <div onClick={handleClear} className="sd-clear-btn">
                    <X size={14} />
                </div>
            )}
            {/* Custom SVG chevron matching the background-image specification */}
            <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="14" height="14" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="#101828" 
                strokeOpacity="0.5" 
                strokeWidth="3" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                className={`sd-chevron ${isOpen ? 'is-open' : ''}`}
            >
                <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="sd-menu">
          
          {/* Search Input Area */}
          <div className="sd-search-container">
            <Search size={16} className="sd-search-icon" />
            <input
              type="text"
              ref={inputRef}
              className="sd-search-input"
              placeholder="Search..."
              value={searchTerm}
              aria-label={"Search: " + label}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Options List */}
          <ul className="sd-list" role="listbox">
            {filteredOptions.length === 0 ? (
              <li className="sd-list-item-empty">
                No results found.
              </li>
            ) : (
              filteredOptions.map((option) => (
                <li
                  key={option}
                  role="option"
                  aria-selected={value === option}
                  className={`sd-list-item ${value === option ? 'is-selected' : ''}`}
                  onClick={() => handleSelect(option)}
                >
                  <span className="sd-toggle-text">
                    {option}
                  </span>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}